import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const complianceAgents = [
  { label: 'Governance Audit Agent', path: '/agents/governance-audit', desc: 'Automate policy-as-code compliance and digital transformation audit trails.' },
  { label: 'KYC Onboarding Agent', path: '/agents/kyc-onboarding', desc: 'Patient and customer identity verification and secure onboarding workflows.' },
  { label: 'LLM Judge Agent', path: '/agents/llm-as-judge', desc: 'Evaluate AI output quality and adherence to enterprise compliance standards.' },
  { label: 'Schema Governance', path: '/agents/schema-governance', desc: 'Automate data schema validation and policy enforcement across platforms.' },
];

const faqs = [
  {
    q: 'How do AI agents ensure compliance in regulated industries?',
    a: 'Agents operate within a "harness" that enforces policy-as-code guardrails, maintains immutable audit trails, and includes human-in-the-loop approval gates.',
  },
  {
    q: 'Can Eagentix automate AML and KYC workflows?',
    a: 'Yes. The KYC Onboarding Agent is designed to automate document-based identity verification and compliance checks for financial and healthcare sectors.',
  },
  {
    q: 'What is the role of the LLM Judge in compliance?',
    a: 'The LLM Judge Agent evaluates the outputs of other agents against specific compliance rubrics, flagging potential risks before they reach production.',
  },
];

export default function ComplianceAutomation() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Compliance Automation: Enterprise Agents | Eagentix"
        description="Automate enterprise compliance with purpose-built AI agents. From KYC to policy-as-code governance, Eagentix provides secure and audited AI workflows."
        pathname="/use-cases/compliance-automation-ai"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Use Case · Compliance
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              AI Compliance Automation for Enterprise
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Move from manual audits to automated governance. Eagentix provides enterprise AI agents that ensure every workflow adheres to your security, ethical, and regulatory standards.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Solutions */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Governed AI Workflows
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Purpose-built agents designed for the compliance needs of highly regulated industries.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {complianceAgents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Enterprise Trust Framework
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            How Eagentix ensures AI safety and regulatory compliance at every step.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              {
                title: 'Policy-as-Code',
                desc: 'Define your compliance rules once and enforce them automatically across every agent deployment.',
              },
              {
                title: 'Immutable Audit Trails',
                desc: 'Maintain a complete record of every model input, output, and tool execution for future regulatory review.',
              },
              {
                title: 'Human-in-the-Loop',
                desc: 'Keep expert oversight for high-risk decisions, ensuring AI serves human judgment, not replaces it.',
              },
            ].map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Compliance AI FAQ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready for Secure AI Automation?
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Book a meeting to explore how Eagentix can automate your compliance workflows while reducing risk and operational overhead.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
